// ** React Imports

import Box, { BoxProps } from '@mui/material/Box'
// ** MUI Imports
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import { styled, useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
// ** Third Party Imports
import clsx from 'clsx'
import { Fragment, memo, useCallback, useEffect } from 'react'
// ** Icon Imports
import Icon from 'src/@core/components/icon'
// ** Type Import
import { LayoutProps, NavGroup, NavLink } from 'src/@core/layouts/types'
// ** Utils
import { hasActiveChild, removeChildren } from 'src/@core/layouts/utils'
// ** Configs Import
import themeConfig from 'src/configs/themeConfig'
import Translations from 'src/layouts/components/Translations'
import { useSidenavRouteSelector } from '@/layouts/navigation/useSidenavRouteSelector'
// ** Custom Components Imports
import VerticalNavItems from './VerticalNavItems'

interface Props {
  item: NavGroup
  navHover: boolean
  parent?: NavGroup
  navVisible?: boolean
  groupActive: string[]
  collapsedNavWidth: number
  currentActiveGroup: string[]
  navigationBorderWidth: number
  settings: LayoutProps['settings']
  isSubToSub?: NavGroup | undefined
  saveSettings: LayoutProps['saveSettings']
  setGroupActive: (values: string[]) => void
  setCurrentActiveGroup: (items: string[]) => void
}

const MenuItemTextWrapper = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out'
}))

const VerticalNavGroup = (props: Props) => {
  // ** Props
  const {
    item,
    parent,
    settings,
    navHover,
    navVisible,
    isSubToSub,
    groupActive,
    setGroupActive,
    collapsedNavWidth,
    currentActiveGroup,
    setCurrentActiveGroup,
    navigationBorderWidth
  } = props

  // ** Hooks & Vars
  const theme = useTheme()
  const hasActiveRoute = useSidenavRouteSelector(
    useCallback((pathname: string) => hasActiveChild(item, pathname), [item])
  )
  const { direction, mode, navCollapsed, verticalNavToggleType } = settings

  // ** Accordion menu group open toggle
  const toggleActiveGroup = (item: NavGroup, parent: NavGroup | undefined) => {
    let openGroup = [...groupActive]

    // ** If Group is already open and clicked, close the group
    if (openGroup.includes(item.title)) {
      openGroup.splice(openGroup.indexOf(item.title), 1)

      // If clicked Group has open group children, Also remove those children to close those groups
      if (item.children) {
        removeChildren(item.children as NavLink[], openGroup, currentActiveGroup)
      }
    } else if (parent) {
      // ** If Group clicked is the child of an open group, first remove all the open groups under that parent
      if (parent.children) {
        removeChildren(parent.children as NavLink[], openGroup, currentActiveGroup)
      }

      // ** After removing all the open groups under that parent, add the clicked group to open group array
      if (!openGroup.includes(item.title)) {
        openGroup.push(item.title)
      }
    } else {
      // ** If clicked on another group that is not active or open, create openGroup array from scratch
      openGroup = []

      // ** push Current Active Group To Open Group array
      if (currentActiveGroup.every(elem => groupActive.includes(elem))) {
        openGroup.push(...currentActiveGroup)
      }

      // ** Push current clicked group item to Open Group array
      if (!openGroup.includes(item.title)) {
        openGroup.push(item.title)
      }
    }
    setGroupActive([...openGroup])
  }

  // ** Menu Group Click
  const handleGroupClick = () => {
    const openGroup = [...groupActive]
    if (verticalNavToggleType === 'collapse') {
      if (openGroup.includes(item.title)) {
        openGroup.splice(openGroup.indexOf(item.title), 1)
      } else {
        openGroup.push(item.title)
      }
      setGroupActive([...openGroup])
    } else {
      toggleActiveGroup(item, parent)
    }
  }

  useEffect(() => {
    const nextGroupActive = [...groupActive]
    let changed = false

    if (hasActiveRoute) {
      if (!nextGroupActive.includes(item.title)) {
        nextGroupActive.push(item.title)
        changed = true
      }
    } else {
      const index = nextGroupActive.indexOf(item.title)
      if (index > -1) {
        nextGroupActive.splice(index, 1)
        changed = true
      }
    }

    if (changed) {
      setGroupActive(nextGroupActive)
      setCurrentActiveGroup(nextGroupActive)
    }

    // Empty Active Group When Menu is collapsed and not hovered, to fix issue route change
    if (navCollapsed && !navHover) {
      setGroupActive([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveRoute])

  useEffect(() => {
    if (navCollapsed && !navHover) {
      setGroupActive([])
    }

    if ((navCollapsed && navHover) || (groupActive.length === 0 && !navCollapsed)) {
      setGroupActive([...currentActiveGroup])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navCollapsed, navHover])

  useEffect(() => {
    if (groupActive.length === 0 && !navCollapsed) {
      setGroupActive([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navHover])

  const icon = !item.icon ? themeConfig.navSubItemIcon : item.icon

  const menuGroupCollapsedStyles = navCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 }

  const conditionalColors = () => {
    if (mode === 'semi-dark') {
      return {
        '&:hover': {
          backgroundColor: `rgba(${theme.palette.customColors.dark}, 0.04)`
        },
        '& .MuiTypography-root, & :not(.menu-item-meta) > svg': {
          color: `rgba(${theme.palette.customColors.dark}, 0.6)`
        },
        '&.Mui-selected': {
          backgroundColor: `rgba(${theme.palette.customColors.dark}, 0.08)`,
          '&:hover': {
            backgroundColor: `rgba(${theme.palette.customColors.dark}, 0.12)`
          },
          '& .MuiTypography-root': {
            fontWeight: 500
          },
          '& .MuiTypography-root, & :not(.menu-item-meta) > svg': {
            color: `rgba(${theme.palette.customColors.dark}, 0.87)`
          },
          '& .menu-item-meta > svg': {
            color: `rgba(${theme.palette.customColors.dark}, 0.6)`
          }
        }
      }
    } else {
      return {
        '& .MuiTypography-root, & :not(.menu-item-meta) > svg': {
          color: 'text.secondary'
        },
        '&.Mui-selected': {
          backgroundColor: 'action.hover',
          '&:hover': {
            backgroundColor: 'action.hover'
          },
          '& .MuiTypography-root, & :not(.menu-item-meta) > svg': {
            color: 'text.primary'
          },
          '& .menu-item-meta > svg': {
            color: 'text.secondary'
          }
        }
      }
    }
  }

  return (
    <Fragment>
      <ListItem
        disablePadding
        className='nav-group'
        onClick={handleGroupClick}
        sx={{ mt: 1, px: '0 !important', flexDirection: 'column' }}
      >
        <ListItemButton
          className={clsx({
            'Mui-selected': groupActive.includes(item.title) || currentActiveGroup.includes(item.title)
          })}
          sx={{
            py: 2,
            mx: 3.5,
            borderRadius: 1,
            ...conditionalColors(),
            width: `calc(100% - ${theme.spacing(3.5 * 2)})`,
            transition: 'padding-left .25s ease-in-out, padding-right .25s ease-in-out',
            px: navCollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 22 - 28) / 8 : 4,
            '&.Mui-selected.Mui-focusVisible': {
              backgroundColor: 'action.focus',
              '&:hover': {
                backgroundColor: 'action.focus'
              }
            }
          }}
        >
          <ListItemIcon
            sx={{
              transition: 'margin .25s ease-in-out',
              ...(parent && navCollapsed && !navHover ? {} : { mr: 2 }),
              ...(navCollapsed && !navHover ? { mr: 0 } : {}),
              ...(parent && item.children ? { ml: 1.5, mr: 3.5 } : {})
            }}
          >
            <Icon fontSize='1.25rem' icon={icon as string} />
          </ListItemIcon>
          <MenuItemTextWrapper sx={{ ...menuGroupCollapsedStyles, ...(isSubToSub ? { ml: 2 } : {}) }}>
            <Typography
              {...(navCollapsed && !navHover && { noWrap: true })}
              sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
            >
              <Translations text={item.title} />
            </Typography>
            <Box
              className='menu-item-meta'
              sx={{
                display: 'flex',
                alignItems: 'center',
                '& svg': {
                  transition: 'transform .25s ease-in-out',
                  color: mode === 'semi-dark' ? `rgba(${theme.palette.customColors.dark}, 0.38)` : 'text.disabled',
                  ...(groupActive.includes(item.title) && {
                    transform: direction === 'ltr' ? 'rotate(90deg)' : 'rotate(-90deg)'
                  })
                }
              }}
            >
              {item.badgeContent ? (
                <Chip
                  label={item.badgeContent}
                  color={item.badgeColor || 'primary'}
                  sx={{
                    mr: 1.5,
                    height: 20,
                    fontWeight: 500,
                    '& .MuiChip-label': { px: 1.5, textTransform: 'capitalize' }
                  }}
                />
              ) : null}
              <Icon fontSize='1.125rem' icon={direction === 'ltr' ? 'tabler:chevron-right' : 'tabler:chevron-left'} />
            </Box>
          </MenuItemTextWrapper>
        </ListItemButton>
        <Collapse
          component='ul'
          onClick={e => e.stopPropagation()}
          in={groupActive.includes(item.title)}
          unmountOnExit
          timeout={180}
          sx={{
            pl: 0,
            width: '100%',
            ...menuGroupCollapsedStyles
          }}
        >
          <VerticalNavItems
            {...props}
            parent={item}
            navVisible={navVisible}
            verticalNavItems={item.children}
            isSubToSub={parent && item.children ? item : undefined}
          />
        </Collapse>
      </ListItem>
    </Fragment>
  )
}

export default memo(VerticalNavGroup, (prev, next) => {
  const prevOpen = prev.groupActive?.includes(prev.item.title) ?? false
  const nextOpen = next.groupActive?.includes(next.item.title) ?? false
  if (prevOpen !== nextOpen) return false

  const prevActive = prev.currentActiveGroup?.includes(prev.item.title) ?? false
  const nextActive = next.currentActiveGroup?.includes(next.item.title) ?? false
  if (prevActive !== nextActive) return false

  if (prev.item !== next.item) return false
  if (prev.navHover !== next.navHover) return false
  if (prev.navVisible !== next.navVisible) return false
  if (prev.collapsedNavWidth !== next.collapsedNavWidth) return false
  if (prev.settings?.mode !== next.settings?.mode) return false
  if (prev.settings?.navCollapsed !== next.settings?.navCollapsed) return false

  return true
})
